/**
 * Web file open funnel — mirrors `buxus-frontend/src/util/nativeFileOpenAnalytics.ts`.
 *
 * - **file_open_pressed** — user tapped Open (no I/O yet)
 * - **file_open_started** — fetch/present pipeline entered
 * - **file_open_succeeded** — blob presented (tab or download)
 * - **file_open_failed** — availability, pop-up, fetch, or catalog error
 *
 * **file_open_dismissed** is not emitted on web (no viewer dismiss callback).
 */
import {
  AppEventTypes,
  isConnectivityErrorMessage,
  outcomeFailureProps,
  outcomeSuccessProps,
  type AnalyticsFailureKind,
} from '@buxus/shared'

import { trackEvent } from './webAnalytics'

export type WebFileOpenAnalyticsBase = {
  screen: string
  fileId?: string
  fileName?: string
  fileExt?: string
  title?: string
}

export type WebFileOpenFailurePhase =
  | 'unavailable'
  | 'popup_blocked'
  | 'fetch'
  | 'catalog'
  | 'present'
  | 'unknown'

export function fileExtFromFileName(fileName: string | undefined): string | undefined {
  if (!fileName) return undefined
  const i = fileName.lastIndexOf('.')
  return i >= 0 ? fileName.slice(i + 1).toLowerCase() : undefined
}

export function normalizeStoredFileExtension(fileExt: string | undefined): string | undefined {
  if (!fileExt) return undefined
  const s = fileExt.replace(/^\./, '').toLowerCase()
  return s || undefined
}

export function trackWebFileOpenPressed(
  base: WebFileOpenAnalyticsBase,
  extra?: Record<string, unknown>,
): void {
  trackEvent(AppEventTypes.FILE_OPEN_PRESSED, { ...base, ...extra })
}

export function trackWebFileOpenStarted(
  base: WebFileOpenAnalyticsBase,
  extra?: Record<string, unknown>,
): void {
  trackEvent(AppEventTypes.FILE_OPEN_STARTED, { ...base, ...extra })
}

export function trackWebFileOpenSucceeded(
  base: WebFileOpenAnalyticsBase,
  extra?: Record<string, unknown>,
): void {
  trackEvent(AppEventTypes.FILE_OPEN_SUCCEEDED, {
    ...base,
    ...outcomeSuccessProps(),
    ...extra,
  })
}

function failureKindForFileOpen(
  phase: WebFileOpenFailurePhase,
  errorMessage: string,
): AnalyticsFailureKind {
  if (isConnectivityErrorMessage(errorMessage)) return 'connectivity'
  if (phase === 'fetch') return 'http'
  if (phase === 'catalog' || phase === 'unavailable' || phase === 'popup_blocked') return 'validation'
  return 'unknown'
}

export function trackWebFileOpenFailed(
  base: WebFileOpenAnalyticsBase,
  payload: { errorPhase: WebFileOpenFailurePhase; errorMessage: string },
  extra?: Record<string, unknown>,
): void {
  trackEvent(AppEventTypes.FILE_OPEN_FAILED, {
    ...base,
    ...extra,
    errorPhase: payload.errorPhase,
    ...outcomeFailureProps({
      errorMessage: payload.errorMessage,
      failureKind: failureKindForFileOpen(payload.errorPhase, payload.errorMessage),
    }),
  })
}

export function webFileOpenFailurePhaseFromError(message: string): WebFileOpenFailurePhase {
  if (message === 'FILE_NOT_IN_CATALOG') return 'catalog'
  if (/\bpop-up/i.test(message)) return 'popup_blocked'
  if (/SAS fetch failed/i.test(message)) return 'fetch'
  if (/Could not open a new tab/i.test(message)) return 'present'
  return 'unknown'
}
